import {
  CheckCircle,
  Email,
  Person,
  CreditCard,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Paper,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import LoadingButton from "./components/LoadingButton";
import VerificationCodeInput from "./components/VerificationCodeInput";
import { hostname, getStripeForAccount } from "./utils";

// Wrapper component that loads the quote and creates the Elements provider
export default function PublicQuotePaymentWrapper() {
  const { quoteId } = useParams<{ quoteId: string }>();
  const [quote, setQuote] = useState<any>(null);
  const [branding, setBranding] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [connectedAccount, setConnectedAccount] = useState<string | undefined>(undefined);
  const theme = useTheme();
  const mode = theme.palette.mode;

  // Get the appropriate Stripe instance (platform or connected account for direct charges)
  const stripeInstance = useMemo(() => {
    return getStripeForAccount(connectedAccount);
  }, [connectedAccount]);

  useEffect(() => {
    loadQuoteAndInitialize();
  }, [quoteId]);

  const loadQuoteAndInitialize = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load quote details
      const response = await axios.get(
        `${hostname}/public/quotes/${quoteId}`
      );

      setQuote(response.data.quote);
      setBranding(response.data.branding);

      // Create subscription to get client secret for payment
      const subscribeResponse = await axios.post(
        `${hostname}/public/quotes/${quoteId}/subscribe`,
        {}
      );

      setClientSecret(subscribeResponse.data.clientSecret);
      setSubscriptionId(subscribeResponse.data.subscriptionId);
      // Store connected account for direct charges (whitelabel)
      if (subscribeResponse.data.connectedAccount) {
        setConnectedAccount(subscribeResponse.data.connectedAccount);
      }
    } catch (err: any) {
      console.error("Error loading quote:", err);
      setError(
        err.response?.data?.error || "Failed to load quote. Please check the URL and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography>Loading quote...</Typography>
      </Container>
    );
  }

  if (error || !quote || !clientSecret) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error">{error || "Failed to initialize payment"}</Alert>
      </Container>
    );
  }

  return (
    <Elements
      stripe={stripeInstance}
      options={{
        clientSecret,
        appearance: {
          theme: mode === "dark" ? "night" : "stripe",
        },
      }}
    >
      <PublicQuotePayment
        quote={quote}
        branding={branding}
        subscriptionId={subscriptionId}
        connectedAccount={connectedAccount}
      />
    </Elements>
  );
}

// Inner component that handles the actual payment
function PublicQuotePayment({
  quote,
  branding,
  subscriptionId,
  connectedAccount,
}: {
  quote: any;
  branding: any;
  subscriptionId: string | null;
  connectedAccount?: string;
}) {
  const { quoteId } = useParams<{ quoteId: string }>();
  const stripe = useStripe();
  const elements = useElements();

  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [success, setSuccess] = useState(false);
  const [passwordResetLink, setPasswordResetLink] = useState<string | null>(
    null
  );
  const [verified, setVerified] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [codeSent, setCodeSent] = useState(false);

  const handleSendCode = async () => {
    setSendingCode(true);
    setError(null);

    try {
      await axios.post(`${hostname}/public/quotes/${quoteId}/send-verification`);
      setCodeSent(true);
    } catch (err: any) {
      console.error("Error sending code:", err);
      setError(err.response?.data?.error || "Failed to send verification code");
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async (code: string) => {
    setVerifyingCode(true);
    setError(null);

    try {
      await axios.post(`${hostname}/public/quotes/${quoteId}/verify-code`, {
        code,
      });
      setVerified(true);
      setActiveStep(1); // Move to payment step
    } catch (err: any) {
      console.error("Error verifying code:", err);
      setError(err.response?.data?.error || "Invalid verification code");
      throw err; // Re-throw to let VerificationCodeInput handle it
    } finally {
      setVerifyingCode(false);
    }
  };

  const handlePayment = async () => {
    if (!stripe || !elements || !quote || !subscriptionId) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // First, submit the form to validate payment details
      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message);
      }

      // Confirm payment - subscription already created, just need to confirm payment
      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href, // Required but not used for immediate confirmation
        },
        redirect: "if_required", // Don't redirect, handle in place
      });

      if (confirmError) {
        throw new Error(confirmError.message);
      }

      // Payment confirmed successfully
      // Confirm with backend to create user account
      const confirmPayload: any = {
        subscriptionId: subscriptionId,
      };
      // Include connected account for direct charges (whitelabel)
      if (connectedAccount) {
        confirmPayload.connectedAccount = connectedAccount;
      }
      const confirmResponse = await axios.post(
        `${hostname}/public/quotes/${quoteId}/confirm`,
        confirmPayload
      );

      setPasswordResetLink(confirmResponse.data.passwordResetLink);
      setSuccess(true);
      setActiveStep(2);
    } catch (err: any) {
      console.error("Payment error:", err);
      setError(err.response?.data?.error || err.message || "Payment failed");
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const steps = ["Verify Email", "Payment", "Complete"];

  if (success) {
    // Determine login URL based on branding domain
    const loginUrl = branding?.domain
      ? `https://${branding.domain}/login`
      : '/login';

    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <CheckCircle sx={{ fontSize: 64, color: "success.main", mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Payment Successful!
          </Typography>
          <Typography color="text.secondary" paragraph>
            Your account has been created successfully.
          </Typography>
          <Alert severity="success" sx={{ mt: 3, textAlign: "left" }}>
            <Typography variant="body2" paragraph>
              <strong>You're all set!</strong> Login with your email to start using your account.
            </Typography>
            <Typography variant="body2">
              Email: <strong>{quote?.email}</strong>
            </Typography>
          </Alert>
          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              size="large"
              href={loginUrl}
            >
              Go to Login
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  const pricingData = quote?.pricingData || {};
  const totalAmount = pricingData.totalAmount || pricingData.monthlyPrice || 0;
  const billingPeriod = pricingData.billingPeriod || "monthly";

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          {branding?.title || "Subscription"} Quote
        </Typography>
        <Typography color="text.secondary">
          Complete your subscription purchase
        </Typography>
      </Box>

      {/* Stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Quote Details - Full Width - Always Visible */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quote Details
              </Typography>

              <Box sx={{ mt: 2 }}>
                {/* Customer Info */}
                <Box sx={{ py: 2, borderBottom: 1, borderColor: "divider" }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Customer
                  </Typography>
                  <Typography variant="body1">
                    {quote?.firstName} {quote?.lastName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {quote?.email}
                  </Typography>
                </Box>

                {/* Plan */}
                <Box
                  sx={{
                    py: 2,
                    borderBottom: 1,
                    borderColor: "divider",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Plan
                  </Typography>
                  <Typography variant="body1">
                    {pricingData.tierName || pricingData.plan}
                  </Typography>
                </Box>

                {/* Credits */}
                <Box
                  sx={{
                    py: 2,
                    borderBottom: 1,
                    borderColor: "divider",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Credits per Month
                  </Typography>
                  <Typography variant="body1">
                    {pricingData.creditLimit?.toLocaleString() || "N/A"}
                  </Typography>
                </Box>

                {/* Companies */}
                <Box
                  sx={{
                    py: 2,
                    borderBottom: 1,
                    borderColor: "divider",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Companies
                  </Typography>
                  <Typography variant="body1">
                    {pricingData.companies || 1}
                  </Typography>
                </Box>

                {/* Seats */}
                <Box
                  sx={{
                    py: 2,
                    borderBottom: 1,
                    borderColor: "divider",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Seats
                  </Typography>
                  <Typography variant="body1">
                    {pricingData.seats || 1}
                  </Typography>
                </Box>

                {/* Total */}
                <Box
                  sx={{
                    py: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body1" fontWeight="medium">
                    Total
                  </Typography>
                  <Typography variant="h5">
                    {formatCurrency(totalAmount)}
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 0.5 }}
                    >
                      /{billingPeriod === "annual" ? "year" : "month"}
                    </Typography>
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Step 0: Email Verification */}
        {activeStep === 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Verify Your Email
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  We'll send a 6-digit verification code to:
                </Typography>
                <Typography variant="body1" fontWeight="medium" paragraph>
                  {quote?.email}
                </Typography>

                {!codeSent ? (
                  <LoadingButton
                    variant="contained"
                    onClick={handleSendCode}
                    loading={sendingCode}
                    disabled={sendingCode}
                  >
                    Send Verification Code
                  </LoadingButton>
                ) : (
                  <Box>
                    <Alert severity="info" sx={{ mb: 3 }}>
                      We've sent a 6-digit code to {quote?.email}. Please enter it
                      below to continue.
                    </Alert>
                    <VerificationCodeInput
                      onComplete={handleVerifyCode}
                      disabled={verifyingCode}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Step 1: Payment Form - Full Width */}
        {activeStep === 1 && (
          <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Payment Information
              </Typography>

              <Box sx={{ mt: 3 }}>
                <PaymentElement
                  options={{
                    layout: {
                      type: "tabs",
                      defaultCollapsed: false,
                    },
                    wallets: {
                      applePay: "auto",
                      googlePay: "auto",
                    },
                  }}
                />
              </Box>

              <LoadingButton
                fullWidth
                variant="contained"
                size="large"
                onClick={handlePayment}
                loading={processing}
                disabled={!stripe || processing}
                sx={{ mt: 3 }}
              >
                Pay {formatCurrency(totalAmount)}
              </LoadingButton>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 2, textAlign: "center" }}
              >
                Powered by Stripe • Secure Payment
              </Typography>
            </CardContent>
          </Card>
          </Grid>
        )}
      </Grid>
    </Container>
  );
}
